import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View, type LayoutChangeEvent } from 'react-native';
import { Text } from './Texte';
import { ChampEtiquete } from './ChampEtiquete';
import { GradientButton } from './GradientButton';
import { PiedAction } from './PiedAction';
import { ArrowLeft, ShieldCheck } from './icons';
import { CONTRATS, disponibiliteDepuisTexte, DUREES_CDD, DUREES_STAGE_MOIS, RAYONS, RYTHMES, saisieDepuisIso } from '../config/offres';
import { useRecouvrementClavier } from '../hooks/useRecouvrementClavier';
import { enregistrerPreferences } from '../services/offres';
import { resoudreCommune } from '../services/communeFrance';
import { isolerLtr } from '../i18n/sens';
import { horsFrancais, t } from '../i18n';
import { colonneFormulaire, colors, formes, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import type { ContratCode, DureeCdd, PreferencesOffres, Rythme } from '../types/offres';
import { styleMiroir } from '../i18n/sens';

export interface PreferencesOffresFormProps {
  initiales: PreferencesOffres;
  /** Critères déjà enregistrés : `null` au premier passage. */
  precedentes: PreferencesOffres | null;
  onEnregistre: (prefs: PreferencesOffres) => void;
  onRetour?: () => void;
}

type Erreur = 'metier' | 'ville' | 'villeIntrouvable' | 'contrat' | 'disponibilite';

const MESSAGES: Record<Erreur, string> = {
  metier: 'offres.preferences.metierRequis',
  ville: 'offres.preferences.villeRequise',
  villeIntrouvable: 'offres.preferences.villeIntrouvable',
  contrat: 'offres.preferences.contratRequis',
  disponibilite: 'offres.preferences.disponibiliteInvalide',
};

function Option({
  libelle,
  libelleComplet,
  actif,
  role,
  onPress,
  testID,
}: {
  libelle: string;
  /** Lu par les lecteurs d'écran quand le bouton n'affiche qu'une abréviation. */
  libelleComplet?: string;
  actif: boolean;
  role: 'checkbox' | 'radio';
  onPress: () => void;
  testID?: string;
}) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole={role}
      accessibilityLabel={libelleComplet}
      accessibilityState={{ checked: actif }}
      onPress={onPress}
      style={[styles.option, actif && styles.optionActive]}
    >
      <Text style={[styles.optionTexte, actif && styles.optionTexteActif]}>{libelle}</Text>
    </Pressable>
  );
}

function Message({ erreur }: { erreur: Erreur | undefined }) {
  return erreur ? <Text style={styles.erreur}>{t(MESSAGES[erreur])}</Text> : null;
}

/** Critères de recherche : un seul écran, prérempli depuis le profil. */
export function PreferencesOffresForm({ initiales, precedentes, onEnregistre, onRetour }: PreferencesOffresFormProps) {
  const [metier, setMetier] = useState(initiales.metier);
  const [contrats, setContrats] = useState<ContratCode[]>(initiales.contrats);
  const [ville, setVille] = useState(initiales.ville);
  const [rayonKm, setRayonKm] = useState(initiales.rayonKm);
  const [rythme, setRythme] = useState<Rythme | null>(initiales.rythme);
  const [dureeStageMois, setDureeStageMois] = useState<number | null>(initiales.dureeStageMois);
  const [dureeCdd, setDureeCdd] = useState<DureeCdd | null>(initiales.dureeCdd);
  const [permisB, setPermisB] = useState<boolean | null>(initiales.permisB);
  const [disponibilite, setDisponibilite] = useState(saisieDepuisIso(initiales.disponibleLe));
  const [erreurs, setErreurs] = useState<Partial<Record<Erreur, true>>>({});
  const [enregistrement, setEnregistrement] = useState(false);
  // Commune comprise pour une saisie approchée (« Londres » → Saint-Martin-de-Londres) : montrée, et
  // il faut valider une seconde fois, avec la même saisie, pour la retenir.
  const [villeComprise, setVilleComprise] = useState<{ saisie: string; libelle: string } | null>(null);
  const [echec, setEchec] = useState(false);

  const racine = useRef<View>(null);
  const defilement = useRef<ScrollView>(null);
  const recouvrement = useRecouvrementClavier(racine);
  // Position et hauteur de la zone « ville » dans le contenu (ses parents directs sont le contenu).
  const zoneVille = useRef({ y: 0, hauteur: 0 });
  const vue = useRef({ decalage: 0, hauteur: 0 });
  const champActif = useRef<'ville' | 'disponibilite' | null>(null);
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Amène le champ actif au-dessus du clavier. Différé : la place sous le contenu (recouvrement) doit
  // être posée avant de défiler, sinon on s'arrête trop tôt.
  const montrer = useCallback((champ: 'ville' | 'disponibilite', recouvre: number) => {
    if (minuteur.current) clearTimeout(minuteur.current);
    minuteur.current = setTimeout(() => {
      if (champ === 'disponibilite') {
        // Dernier champ : le bas du contenu est le bon endroit.
        defilement.current?.scrollToEnd({ animated: true });
        return;
      }
      const { y, hauteur } = zoneVille.current;
      const bas = y + hauteur + spacing.lg;
      const visibleHaut = vue.current.decalage;
      const visibleBas = vue.current.decalage + vue.current.hauteur - recouvre;
      if (y - spacing.lg < visibleHaut) defilement.current?.scrollTo({ y: Math.max(0, y - spacing.lg), animated: true });
      else if (bas > visibleBas) defilement.current?.scrollTo({ y: bas - (vue.current.hauteur - recouvre), animated: true });
    }, 60);
  }, []);

  useEffect(() => {
    if (recouvrement === 0) champActif.current = null;
    else if (champActif.current) montrer(champActif.current, recouvrement);
  }, [recouvrement, montrer]);
  useEffect(() => () => { if (minuteur.current) clearTimeout(minuteur.current); }, []);

  // Le focus arrive avant le clavier : on retient le champ, l'effet ci-dessus défile à l'ouverture.
  // Clavier déjà ouvert (passage d'un champ à l'autre) : on défile tout de suite.
  const surFocus = (champ: 'ville' | 'disponibilite') => () => {
    champActif.current = champ;
    if (recouvrement > 0) montrer(champ, recouvrement);
  };

  const alternance = contrats.includes('alternance');
  const stage = contrats.includes('stage');
  const cdd = contrats.includes('cdd');
  // Le bouton du pied est toujours visible : un champ en erreur peut être hors écran. Le pied le dit.
  const aVerifier = Object.keys(erreurs).length > 0 || (villeComprise !== null && villeComprise.saisie === ville.trim());
  // Seul le CDI : rien de plus à choisir (ni rythme ni durée). Dit en une phrase plutôt que de laisser
  // un vide qui ressemble à un oubli.
  const cdiSeul = contrats.length === 1 && contrats[0] === 'cdi';

  const basculerContrat = (c: ContratCode) =>
    setContrats((avant) => (avant.includes(c) ? avant.filter((x) => x !== c) : CONTRATS.filter((x) => x === c || avant.includes(x))));

  const valider = async () => {
    const dispoTexte = disponibilite.trim();
    const disponibleLe = dispoTexte ? disponibiliteDepuisTexte(dispoTexte) : null;
    const trouvees: Partial<Record<Erreur, true>> = {
      ...(metier.trim() ? {} : { metier: true }),
      ...(ville.trim() ? {} : { ville: true }),
      ...(contrats.length > 0 ? {} : { contrat: true }),
      ...(dispoTexte && !disponibleLe ? { disponibilite: true } : {}),
    };
    setErreurs(trouvees);
    setEchec(false);
    if (Object.keys(trouvees).length > 0) return;

    const prefs: PreferencesOffres = {
      metier: metier.trim(),
      contrats,
      ville: ville.trim(),
      rayonKm,
      rythme: alternance ? rythme : null,
      // Comme le rythme : une durée choisie puis un contrat décoché n'est pas enregistrée.
      dureeStageMois: stage ? dureeStageMois : null,
      dureeCdd: cdd ? dureeCdd : null,
      permisB,
      disponibleLe,
    };
    setEnregistrement(true);
    try {
      const commune = await resoudreCommune(prefs.ville);
      if (commune.statut === 'introuvable') {
        setErreurs({ villeIntrouvable: true });
        return;
      }
      if (commune.statut === 'approchee' && villeComprise?.saisie !== prefs.ville) {
        setVilleComprise({ saisie: prefs.ville, libelle: `${commune.nom} (${commune.departement})` });
        return;
      }
      await enregistrerPreferences(prefs, precedentes);
      onEnregistre(prefs);
    } catch {
      setEchec(true);
    } finally {
      setEnregistrement(false);
    }
  };

  // Le clavier couvrait la date, dernier champ du formulaire (retours d'essai des 17 et 25/10/2026).
  // La correction native (KeyboardAvoidingView, inset automatique de la ScrollView) se comporte
  // différemment selon iOS, Android bord à bord (SDK 54) et la barre d'onglets : on fait donc
  // nous-mêmes, partout pareil. Le recouvrement réel du clavier s'ajoute sous le contenu, puis on
  // défile jusqu'au champ actif. Le bouton est dans le pied fixe (PiedAction), rangé tant que le
  // clavier est ouvert. Le clavier numérique n'a pas de touche de validation : glisser le doigt ou
  // toucher hors d'un champ le referme.
  return (
    <View ref={racine} collapsable={false} style={styles.racine}>
      <ScrollView
        ref={defilement}
        testID="pref-defilement"
        style={styles.defilement}
        contentContainerStyle={[styles.contenu, colonneFormulaire, { paddingBottom: spacing.xl + recouvrement }]}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        scrollEventThrottle={16}
        onScroll={(e) => {
          vue.current.decalage = e.nativeEvent.contentOffset.y;
        }}
        onLayout={(e: LayoutChangeEvent) => {
          vue.current.hauteur = e.nativeEvent.layout.height;
        }}
      >
        <View style={styles.enteteLigne}>
          {onRetour && precedentes ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('offres.preferences.retour')}
              onPress={onRetour}
              style={styles.retour}
            >
              <View style={styleMiroir()}><ArrowLeft size={22} color={colors.night} /></View>
            </Pressable>
          ) : null}
          <View style={styles.entete}>
            <Text style={styles.titre} accessibilityRole="header">
              {t('offres.preferences.titre')}
            </Text>
            <Text style={styles.sousTitre}>{t('offres.preferences.sousTitre')}</Text>
            {horsFrancais() ? (
              <Text style={styles.aide} testID="pref-france-uniquement">
                {t('offres.franceUniquement')}
              </Text>
            ) : null}
          </View>
        </View>

        <View style={styles.champ}>
          <ChampEtiquete
            label={t('offres.preferences.metier')}
            value={metier}
            onChangeText={setMetier}
            placeholder={t('offres.preferences.metierPlaceholder')}
            ltr
            testID="pref-metier"
          />
          <Message erreur={erreurs.metier ? 'metier' : undefined} />
        </View>

        <View style={styles.champ}>
          <View style={styles.titreLigne}>
            <Text style={styles.libelle}>{t('offres.preferences.contrat')}</Text>
            <Text style={styles.indice}>{`· ${t('offres.preferences.contratMulti')}`}</Text>
          </View>
          <View style={styles.options}>
            {CONTRATS.map((c) => (
              <Option key={c} role="checkbox" libelle={t(`offres.contrats.${c}`)} actif={contrats.includes(c)} onPress={() => basculerContrat(c)} />
            ))}
          </View>
          <Message erreur={erreurs.contrat ? 'contrat' : undefined} />
        </View>

        {alternance ? (
          <View style={[styles.champ, styles.section]} testID="pref-section-rythme">
            <Text style={styles.libelle}>{t('offres.preferences.rythme')}</Text>
            <Text style={styles.aide}>{t('offres.preferences.rythmeAide')}</Text>
            <View style={styles.options} accessibilityRole="radiogroup" testID="pref-rythmes">
              {RYTHMES.map((r) => (
                <Option
                  key={r}
                  role="radio"
                  libelle={t(`offres.rythmes.${r}`)}
                  libelleComplet={t(`offres.rythmesComplets.${r}`)}
                  actif={rythme === r}
                  onPress={() => setRythme((avant) => (avant === r ? null : r))}
                />
              ))}
              <Option
                testID="pref-rythme-indifferent"
                role="radio"
                libelle={t('offres.preferences.peuImporte')}
                actif={rythme === null}
                onPress={() => setRythme(null)}
              />
            </View>
          </View>
        ) : null}

        {stage ? (
          <View style={[styles.champ, styles.section]} testID="pref-section-stage">
            <Text style={styles.libelle}>{t('offres.preferences.dureeStage')}</Text>
            <Text style={styles.aide}>{t('offres.preferences.dureeAide')}</Text>
            <View style={styles.options} accessibilityRole="radiogroup" testID="pref-durees-stage">
              {DUREES_STAGE_MOIS.map((mois) => (
                <Option
                  key={mois}
                  testID={`pref-stage-${mois}`}
                  role="radio"
                  libelle={t(`offres.dureesStage.${mois}`)}
                  actif={dureeStageMois === mois}
                  onPress={() => setDureeStageMois((avant) => (avant === mois ? null : mois))}
                />
              ))}
              <Option
                testID="pref-stage-indifferent"
                role="radio"
                libelle={t('offres.preferences.peuImporte')}
                actif={dureeStageMois === null}
                onPress={() => setDureeStageMois(null)}
              />
            </View>
          </View>
        ) : null}

        {cdd ? (
          <View style={[styles.champ, styles.section]} testID="pref-section-cdd">
            <Text style={styles.libelle}>{t('offres.preferences.dureeCdd')}</Text>
            <Text style={styles.aide}>{t('offres.preferences.dureeAide')}</Text>
            <View style={styles.options} accessibilityRole="radiogroup" testID="pref-durees-cdd">
              {DUREES_CDD.map((d) => (
                <Option
                  key={d}
                  testID={`pref-cdd-${d}`}
                  role="radio"
                  libelle={t(`offres.dureesCdd.${d}`)}
                  actif={dureeCdd === d}
                  onPress={() => setDureeCdd((avant) => (avant === d ? null : d))}
                />
              ))}
              <Option
                testID="pref-cdd-indifferent"
                role="radio"
                libelle={t('offres.preferences.peuImporte')}
                actif={dureeCdd === null}
                onPress={() => setDureeCdd(null)}
              />
            </View>
          </View>
        ) : null}

        {cdiSeul ? (
          <Text style={styles.sousTitre} testID="pref-cdi-seul">
            {t('offres.preferences.cdiSeul')}
          </Text>
        ) : null}

        <View
          testID="pref-zone-ville"
          style={styles.champ}
          onLayout={(e) => {
            zoneVille.current = { y: e.nativeEvent.layout.y, hauteur: e.nativeEvent.layout.height };
          }}
        >
          <ChampEtiquete
            label={t('offres.preferences.ville')}
            value={ville}
            onChangeText={(texte) => {
              setVille(texte);
              setVilleComprise(null);
            }}
            placeholder={t('offres.preferences.villePlaceholder')}
            ltr
            onFocus={surFocus('ville')}
            testID="pref-ville"
          />
          <Message erreur={erreurs.ville ? 'ville' : erreurs.villeIntrouvable ? 'villeIntrouvable' : undefined} />
          {villeComprise && villeComprise.saisie === ville.trim() ? (
            <Text style={styles.villeComprise} testID="pref-ville-comprise">
              {t('offres.preferences.villeRetenue', { commune: isolerLtr(villeComprise.libelle) })}
            </Text>
          ) : null}
          <View style={styles.rayonLigne}>
            <Text style={styles.libelle}>{t('offres.preferences.rayon')}</Text>
            <View style={styles.options} accessibilityRole="radiogroup">
              {RAYONS.map((km) => (
                <Option key={km} role="radio" libelle={t('offres.rayonKm', { km })} actif={rayonKm === km} onPress={() => setRayonKm(km)} />
              ))}
            </View>
          </View>
        </View>

        <View style={styles.reponses}>
          <View style={styles.reponsesEntete}>
            <ShieldCheck size={20} color={colors.night} />
            <Text style={styles.reponsesTitre}>{t('offres.preferences.reponsesTitre')}</Text>
          </View>

          <View style={styles.champ}>
            <Text style={styles.libelle}>{t('offres.preferences.permis')}</Text>
            <View style={styles.options} accessibilityRole="radiogroup">
              <Option role="radio" libelle={t('offres.preferences.oui')} actif={permisB === true} onPress={() => setPermisB(true)} />
              <Option role="radio" libelle={t('offres.preferences.non')} actif={permisB === false} onPress={() => setPermisB(false)} />
              <Option role="radio" libelle={t('offres.preferences.aRenseigner')} actif={permisB === null} onPress={() => setPermisB(null)} />
            </View>
          </View>

          <View style={styles.champ}>
            <ChampEtiquete
              label={t('offres.preferences.disponibilite')}
              value={disponibilite}
              onChangeText={setDisponibilite}
              placeholder={t('offres.preferences.disponibilitePlaceholder')}
              keyboardType="numbers-and-punctuation"
              maxLength={10}
              ltr
              onFocus={surFocus('disponibilite')}
              testID="pref-disponibilite"
            />
            <Message erreur={erreurs.disponibilite ? 'disponibilite' : undefined} />
          </View>
        </View>
      </ScrollView>

      <PiedAction testID="pref-pied">
        {echec ? (
          <Text style={styles.erreur} accessibilityRole="alert">
            {t('offres.preferences.erreurEnregistrement')}
          </Text>
        ) : aVerifier ? (
          <Text testID="pref-verifier" style={styles.erreur} accessibilityRole="alert">
            {t('offres.preferences.verifier')}
          </Text>
        ) : null}
        <GradientButton
          label={t('offres.preferences.voir')}
          variante="cta"
          hauteur="lg"
          withArrow
          loading={enregistrement}
          onPress={valider}
          testID="pref-voir"
        />
      </PiedAction>
    </View>
  );
}

const styles = StyleSheet.create({
  racine: { flex: 1 },
  defilement: { flex: 1 },
  contenu: { padding: screenPadding, gap: spacing.lg, paddingBottom: spacing.xl },
  enteteLigne: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md },
  // Carré blanc aux coins arrondis, comme le retour des autres écrans refaits.
  retour: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: formes.rayon.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.white,
    ...shadow.card,
  },
  entete: { flex: 1, minWidth: 0, gap: spacing.xs },
  titre: { fontSize: typography.h1.size, lineHeight: typography.h1.lineHeight, fontWeight: '800', color: colors.navy },
  sousTitre: { fontSize: typography.body.size, lineHeight: typography.body.lineHeight, color: colors.grey500 },
  champ: { gap: spacing.sm },
  titreLigne: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'baseline', gap: spacing.xs },
  libelle: { fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  indice: { fontSize: typography.small.size, fontWeight: '600', color: colors.grey500 },
  aide: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  section: { padding: spacing.lg, borderRadius: formes.rayon.lg, backgroundColor: colors.white, ...shadow.card },
  rayonLigne: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: spacing.sm },
  options: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  option: {
    minHeight: formes.cible,
    minWidth: formes.cible,
    paddingHorizontal: spacing.md,
    borderRadius: formes.rayon.xl,
    borderWidth: 1.5,
    borderColor: colors.bordureClaire,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionActive: { backgroundColor: colors.night, borderColor: colors.night },
  optionTexte: { fontSize: typography.bodyMd.size, fontWeight: '600', color: colors.night },
  optionTexteActif: { color: colors.white, fontWeight: '700' },
  reponses: { gap: spacing.md, padding: spacing.lg, borderRadius: formes.rayon.lg, backgroundColor: colors.white, ...shadow.card },
  reponsesEntete: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  reponsesTitre: { flex: 1, fontSize: typography.h3.size, fontWeight: typography.h3.weight, color: colors.night },
  erreur: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.error },
  villeComprise: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.night },
});
